import { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '../../utils/api';
import {
  FaFolderPlus,
  FaSearch,
  FaTimes,
  FaFolder,
  FaEdit,
  FaTrashAlt,
  FaSave,
  FaLayerGroup,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationCircle,
} from 'react-icons/fa';

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Form states
  const [addFormData, setAddFormData] = useState({
    name: '',
    description: '',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
  });

  const [formSubmitting, setFormSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const showNotification = (msg, isSuccess = true) => {
    if (isSuccess) {
      setSuccess(msg);
      setError('');
      setTimeout(() => setSuccess(''), 4000);
    } else {
      setError(msg);
      setSuccess('');
      setTimeout(() => setError(''), 4000);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/categories');
      if (!res.ok) {
        throw new Error('Failed to load categories');
      }
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const q = searchQuery.toLowerCase();
    return categories.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.slug?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
    );
  }, [categories, searchQuery]);

  // Total blogs mapped
  const totalBlogsTagged = useMemo(() => {
    return categories.reduce((acc, curr) => acc + (curr.blogCount || 0), 0);
  }, [categories]);

  // Open Add Modal
  const handleOpenAddModal = () => {
    setAddFormData({ name: '', description: '' });
    setModalError('');
    setAddModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (cat) => {
    setEditingCategory(cat);
    setEditFormData({
      name: cat.name || '',
      description: cat.description || '',
    });
    setModalError('');
    setEditModalOpen(true);
  };

  // Submit Add Category
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!addFormData.name.trim()) {
      setModalError('Category name is required.');
      return;
    }

    try {
      setFormSubmitting(true);
      setModalError('');

      const res = await apiFetch('/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addFormData),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to create category');
      }

      setAddModalOpen(false);
      showNotification(`Category "${data.name}" created successfully!`);
      loadCategories();
    } catch (err) {
      setModalError(err.message || 'Failed to create category');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Submit Edit Category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      setModalError('Category name is required.');
      return;
    }

    try {
      setFormSubmitting(true);
      setModalError('');

      const res = await apiFetch(`/api/categories/${editingCategory._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to update category');
      }

      setEditModalOpen(false);
      showNotification(`Category updated successfully!`);
      loadCategories();
    } catch (err) {
      setModalError(err.message || 'Failed to update category');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Trigger Delete
  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setFormSubmitting(true);
      const res = await apiFetch(`/api/categories/${categoryToDelete._id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to delete category');
      }

      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      showNotification('Category deleted successfully.');
      loadCategories();
    } catch (err) {
      showNotification(err.message || 'Failed to delete category', false);
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Categories</p>
              <h3 className="mt-1 text-2xl font-bold text-slate-900">{categories.length}</h3>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <FaLayerGroup className="text-xl" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Active topics configured for blogs</p>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tagged Articles</p>
              <h3 className="mt-1 text-2xl font-bold text-slate-900">{totalBlogsTagged}</h3>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <FaFileAlt className="text-xl" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Articles categorized across all topics</p>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sync Status</p>
              <h3 className="mt-1 text-xl font-bold text-emerald-600 flex items-center gap-1.5">
                <FaCheckCircle className="text-lg" /> Live in Editor
              </h3>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <FaFolder className="text-xl" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">Directly selectable in Blog creation</p>
        </div>
      </div>

      {/* Notifications */}
      {success && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/90 p-4 text-sm font-medium text-emerald-800 shadow-2xs">
          <FaCheckCircle className="text-base text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50/90 p-4 text-sm font-medium text-red-800 shadow-2xs">
          <FaExclamationCircle className="text-base text-red-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Table Card */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs">
        {/* Table Header / Action Bar */}
        <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Categories Management</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Add, edit, or delete categories used for classifying blog articles.
            </p>
          </div>

          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search categories..."
                className="w-full sm:w-60 rounded-xl border border-slate-200 bg-slate-50/80 py-2 pl-9 pr-3 text-xs sm:text-sm text-slate-800 placeholder-slate-400 transition focus:border-amber-400 focus:bg-white focus:outline-none"
              />
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={handleOpenAddModal}
              className="flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2 text-xs sm:text-sm font-semibold text-slate-950 shadow-xs transition hover:bg-amber-300"
            >
              <FaFolderPlus className="text-sm" />
              <span>Add Category</span>
            </button>
          </div>
        </div>

        {/* Categories Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/75 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3.5">Category Name</th>
                <th className="px-6 py-3.5">URL Slug</th>
                <th className="px-6 py-3.5">Description</th>
                <th className="px-6 py-3.5 text-center">Blogs Tagged</th>
                <th className="px-6 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
                    <p className="mt-2 text-xs font-medium">Loading categories...</p>
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    <FaFolder className="mx-auto mb-2 text-3xl text-slate-300" />
                    <p className="font-semibold text-slate-700">No categories found</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {searchQuery ? 'Try matching another search term.' : 'Click "Add Category" above to create your first one.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCategories.map((cat) => (
                  <tr key={cat._id} className="transition hover:bg-slate-50/80">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 font-bold border border-amber-200/60">
                          <FaFolder className="text-sm" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">{cat.name}</p>
                          <p className="text-[11px] text-slate-400">ID: {cat._id?.slice(-6)}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700 border border-slate-200">
                        {cat.slug}
                      </span>
                    </td>

                    <td className="px-6 py-4 max-w-xs truncate text-xs text-slate-600">
                      {cat.description || <span className="italic text-slate-400">No description provided</span>}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-blue-700 border border-blue-200/60">
                        {cat.blogCount || 0} articles
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(cat)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-amber-400 hover:text-amber-600 hover:shadow-xs"
                          title="Edit Category"
                        >
                          <FaEdit className="text-xs" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryToDelete(cat);
                            setDeleteModalOpen(true);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-red-400 hover:text-red-600 hover:shadow-xs"
                          title="Delete Category"
                        >
                          <FaTrashAlt className="text-xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= ADD MODAL ================= */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-xs">
          <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <FaFolderPlus className="text-base" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Add New Category</h3>
                  <p className="text-xs text-slate-400">Available instantly in Blog creation</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAddModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            {modalError && (
              <div className="mt-3.5 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                {modalError}
              </div>
            )}

            <form onSubmit={handleCreateCategory} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. GST & Tax Compliances"
                  value={addFormData.name}
                  onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Short Description <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  rows="3"
                  placeholder="Briefly describe what articles belong to this topic..."
                  value={addFormData.description}
                  onChange={(e) => setAddFormData({ ...addFormData, description: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="flex items-center gap-1.5 rounded-xl bg-amber-400 px-4 py-2 text-xs font-bold text-slate-950 shadow-xs hover:bg-amber-300 disabled:opacity-50"
                >
                  <FaSave className="text-xs" />
                  <span>{formSubmitting ? 'Saving...' : 'Create Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= EDIT MODAL ================= */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-xs">
          <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <FaEdit className="text-base" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Edit Category</h3>
                  <p className="text-xs text-slate-400">Updating name syncs with existing blogs</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            {modalError && (
              <div className="mt-3.5 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                {modalError}
              </div>
            )}

            <form onSubmit={handleUpdateCategory} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Short Description
                </label>
                <textarea
                  rows="3"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 transition focus:border-amber-400 focus:bg-white focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="flex items-center gap-1.5 rounded-xl bg-amber-400 px-4 py-2 text-xs font-bold text-slate-950 shadow-xs hover:bg-amber-300 disabled:opacity-50"
                >
                  <FaSave className="text-xs" />
                  <span>{formSubmitting ? 'Saving...' : 'Update Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      {deleteModalOpen && categoryToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-xs">
          <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                <FaTrashAlt className="text-lg" />
              </div>
              <h3 className="mt-3 text-base font-bold text-slate-900">Delete Category?</h3>
              <p className="mt-1 text-xs text-slate-500">
                Are you sure you want to delete <span className="font-bold text-slate-800">"{categoryToDelete.name}"</span>?
                {categoryToDelete.blogCount > 0 && (
                  <span className="block mt-1 text-amber-600 font-semibold">
                    Note: {categoryToDelete.blogCount} articles are currently tagged with this category.
                  </span>
                )}
              </p>
            </div>

            <div className="mt-5 flex items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setCategoryToDelete(null);
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={formSubmitting}
                onClick={handleConfirmDelete}
                className="rounded-xl bg-red-600 px-4 py-2 text-xs font-bold text-white shadow-xs hover:bg-red-700 disabled:opacity-50"
              >
                {formSubmitting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
